import { UPDATE_GUEST_SHIPPING_ADDRESS_CALLBACK_MUTATION } from '@/components/Address/graphql/mutations/updateGuestShippingAddressCallback.gql';
import { CI, getAddressMetaKey, getToken } from '@/lib';
import { useCallback, useRef } from 'react';
import { useStoreActions, useStoreState } from '@/store';
import type {
  EmptyObject,
  MappedAddressValues,
} from '@/lib/utils/shipping/types';
import type { ErrorReason } from '@paypalcorp/ppclientinteractions';
import { addressAreEqualV2, normalizeAddressForMerchantV2 } from '@/lib';
import type {
  UpdateGuestShippingAddressCallbackMutationMutation,
  UpdateGuestShippingAddressCallbackMutationMutationVariables,
} from '@/generated/graphql';
import { apolloClient } from '@/lib/apollo';
import { latencyLogger, useLatencyLogger } from '@/lib/utils/shipping/fpti';
import { logger } from '@/lib/logger';
import { filterOutNonApplicableAddressFields } from '@/components/Form/util/addressOperations';
import { getShippingAddress } from '@/lib/utils/shipping';
// @ts-expect-error - utils.js file doesn't have TypeScript declarations
import { isShippingCallbackRequiredAddressValid } from '@/components/Address/utils';

// Type for the mapped address object that will be sent to the mutation
type MappedAddressForMutation = {
  adminArea1?: string;
  adminArea2?: string;
  postalCode?: string;
};

export type UpdateGuestShippingAddressCallbackCart =
  UpdateGuestShippingAddressCallbackMutationMutation['updateGuestShippingAddressCallback'];

type ExecuteResult =
  | { didCallbackFire: false }
  | {
      didCallbackFire: true;
      checkoutSessionData: UpdateGuestShippingAddressCallbackCart;
    };

type ExecuteFunction = (
  shippingAddress: MappedAddressValues | EmptyObject,
  toggleLoadingSpinner: (spinner: string | null) => void,
) => Promise<ExecuteResult>;

/**
 * Maps shipping address error issues to appropriate CI error reasons
 * @param errorIssue - The error issue from GraphQL error details
 * @returns The appropriate CI error reason
 */
const getShippingAddressCiErrorReason = (errorIssue?: string): ErrorReason => {
  switch (errorIssue) {
    case 'ADDRESS_ERROR':
    case 'COUNTRY_ERROR':
    case 'STATE_ERROR':
    case 'ZIP_ERROR':
      // These are user input validation errors (422 status)
      return CI.ErrorReason.User;

    case 'CALLBACK_SERVER_ERROR':
      // Internal server error (500 status)
      return CI.ErrorReason.Server;

    case 'CALLBACK_TIMEOUT_ERROR':
      // Timeout error (500 status) - typically categorized as network
      return CI.ErrorReason.Network;

    default:
      // Default to ThirdParty for unknown errors to maintain existing behavior
      return CI.ErrorReason.ThirdParty;
  }
};

const mapAddressForMutation = (
  shippingAddress: MappedAddressValues | EmptyObject,
) => {
  // Filter fields so that the address provided to the mutation
  // contains only the fields that are applicable to the country.
  // The address sent along with the onboard mutation is also filtered in the same
  // way and this ensures that the address used in the callback is the same as the
  // address used in the onboard mutation.
  const filteredAddress = filterOutNonApplicableAddressFields(
    shippingAddress,
    getAddressMetaKey(shippingAddress.country),
  );

  const mappedFilteredAddress = Object.entries(
    filteredAddress,
  ).reduce<MappedAddressForMutation>((acc, [key, value]) => {
    switch (key) {
      case 'state':
        acc.adminArea1 = value;
        break;
      case 'city':
        acc.adminArea2 = value;
        break;
      case 'postalCode':
        acc.postalCode = value;
        break;
    }

    return acc;
  }, {});

  return mappedFilteredAddress;
};

/**
 * Hook for server-side shipping address change callback.
 * Returns a function: (address, toggleLoadingSpinner) => Promise<ExecuteResult>
 */
export const useServerSideShippingAddressChangeCallback =
  (): ExecuteFunction => {
    const token = getToken();
    const lastCallbackShippingAddress = useStoreState(
      ({ api }) => api.lastCallbackShippingAddress,
    );
    const setCartData = useStoreActions(({ api }) => api.setCartData);

    // Initialize ref with store value (persistence across reload) or empty object (first time)
    const previousShippingAddressRef = useRef(
      lastCallbackShippingAddress || {},
    );

    const addressServerSideLatencyLogger = latencyLogger(
      'address',
      'server_side',
    );
    const {
      startLogging: startServerSideAddressLatencyLogging,
      finishLogging: finishServerSideAddressLatencyLogging,
    } = useLatencyLogger(addressServerSideLatencyLogger);

    const execute = useCallback<ExecuteFunction>(
      async (shippingAddress, toggleLoadingSpinner) => {
        const hasAddress = Object.keys(shippingAddress).length > 0;
        const hasCountry = shippingAddress.country;
        const flowStartEventName = `sssc_shipping_address_mutation_start_${
          hasAddress ? 'has_address' : 'no_address'
        }_${hasCountry ? 'has_country' : 'no_country'}`;

        logger.info(flowStartEventName, {
          addressCountry: shippingAddress?.country,
          hasAddress: hasAddress,
          hasCountry: hasCountry,
          token,
        });

        // The specific address utility used here is not important.
        // What matters is that we use the same utility for both normalization and comparison,
        // ensuring consistency when checking if the address has changed.
        const currentShippingAddress =
          normalizeAddressForMerchantV2(shippingAddress);

        const hasAddressChanged = !addressAreEqualV2(
          currentShippingAddress,
          previousShippingAddressRef.current,
        );

        // Don't fire callback if address hasn't changed
        if (!hasAddressChanged) {
          logger.info('sssc_shipping_address_mutation_skipped', {
            reason: 'address_unchanged',
            token,
          });
          return { didCallbackFire: false };
        }

        // Don't fire callback if required shipping address fields are not valid
        // This prevents callback from firing when only country is filled out
        const currentShippingFormState = getShippingAddress();

        if (!isShippingCallbackRequiredAddressValid(currentShippingFormState)) {
          return { didCallbackFire: false };
        }
        logger.info(
          'sssc_shipping_address_mutation_executing_with_address_change',
          { token },
        );

        previousShippingAddressRef.current = currentShippingAddress;
        setCartData({
          lastCallbackShippingAddress: currentShippingAddress,
        });

        try {
          // Show the loading spinner only when the shipping address
          // changes and this callback executes.
          toggleLoadingSpinner('spinner.fetchingShipping');
          CI.start(CI.Event.GUEST_SHIPPING_ADDRESS_CHANGE_CALLBACK);
          startServerSideAddressLatencyLogging();
          const result = await apolloClient.mutate<
            UpdateGuestShippingAddressCallbackMutationMutation,
            UpdateGuestShippingAddressCallbackMutationMutationVariables
          >({
            mutation: UPDATE_GUEST_SHIPPING_ADDRESS_CALLBACK_MUTATION,
            variables: {
              address: {
                ...mapAddressForMutation(shippingAddress),
                countryCode: shippingAddress.country,
              },
              token,
            },
          });

          CI.endWith(
            CI.Event.GUEST_SHIPPING_ADDRESS_CHANGE_CALLBACK,
            CI.Status.Ok,
          );

          const checkoutSessionData =
            result.data?.updateGuestShippingAddressCallback;
          logger.info('sssc_shipping_address_mutation_success', {
            cartItemsCount: checkoutSessionData?.cart?.items?.length || 0,
            hasCartData: !!checkoutSessionData?.cart,
            shippingMethodsCount:
              checkoutSessionData?.cart?.shippingMethods?.length || 0,
            token,
          });

          return {
            checkoutSessionData,
            didCallbackFire: true,
          };
        } catch (error: any) {
          const errorIssue =
            error.graphQLErrors?.[0]?.data?.details?.[0]?.issue;
          const errorReason = getShippingAddressCiErrorReason(errorIssue);

          logger.error('sssc_shipping_address_mutation_error', {
            errorIssue,
            errorMessage: error.message,
            errorReason,
            graphQLErrorsCount: error.graphQLErrors?.length || 0,
            networkError: !!error.networkError,
            token,
          });

          CI.endWith(
            CI.Event.GUEST_SHIPPING_ADDRESS_CHANGE_CALLBACK,
            CI.Status.Error,
            errorReason,
          );
          // This error description will be used to map to a UI error message.
          // If no error description is present, a generic error message will be used.
          throw new Error(errorIssue);
        } finally {
          finishServerSideAddressLatencyLogging();
          toggleLoadingSpinner(null);
        }
      },
      [token],
    );

    return execute;
  };
